/* eslint-disable react/no-unstable-nested-components */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Package,
  FileText,
  Users,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Heart,
  Activity,
  Compass,
  Briefcase,
  FolderOpen,
  Megaphone,
  ShieldCheck,
  Zap,
  HelpCircle,
  Star,
  PenLine,
  GitBranch
} from 'lucide-react';

const AdminLayout = () => {
  const { admin, logout, isSuperAdmin, hasPermission } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Close mobile sidebar on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Check if user has permission for a feature (use context method)
  const checkPerm = (permissionKey) => hasPermission(permissionKey);

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    { path: '/admin/packages', label: 'Tour Catalog', icon: Package, permission: 'packages' },
    { path: '/admin/theme-tours', label: 'Theme Tours', icon: Compass, permission: 'theme_tours' },
    { path: '/admin/theme-tour-categories', label: 'Tour Categories', icon: FolderOpen, permission: 'theme_tours' },
    { path: '/admin/lead-pipeline', label: 'Lead Pipeline', icon: GitBranch, permission: 'leads' },
    { path: '/admin/promotions', label: 'Promotions', icon: Zap, permission: 'promotions' },
    { path: '/admin/reviews', label: 'Reviews', icon: Star, permission: 'content' },
    { path: '/admin/faqs', label: 'FAQs', icon: HelpCircle, permission: 'content' },
    { path: '/admin/blog', label: 'Blog', icon: PenLine, permission: 'content' },
    { path: '/admin/meta-ads', label: 'Meta Ads', icon: Megaphone, permission: 'meta_ads' },
    { path: '/admin/site-pages', label: 'Site Pages', icon: FileText, permission: 'content' },
    { path: '/admin/security', label: 'Security', icon: ShieldCheck, permission: 'security' },
  ];

  const reportItems = [
    { path: '/admin/reports/enquiries', label: 'Enquiry Reports' },
    { path: '/admin/reports/contacts', label: 'Contact Reports' },
    { path: '/admin/reports/packages', label: 'Package Performance' },
    { path: '/admin/reports/leads', label: 'Leads (Get Quote)' },
    { path: '/admin/reports/corporate', label: 'Corporate Enquiries' },
    { path: '/admin/reports/sip-leads', label: 'DNO SIP Plan' },
    { path: '/admin/wedding-enquiries', label: 'Wedding Enquiries' },
  ];

  const isActive = (path) => location.pathname === path;
  const isReportActive = () => location.pathname.includes('/admin/reports');

  // Sidebar content component (shared between desktop and mobile)
  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div className={`p-4 flex items-center justify-between border-b border-slate-700 ${isMobile ? '' : ''}`}>
        {(sidebarOpen || isMobile) && (
          <div>
            <h1 className="text-lg font-bold text-red-500">Dnotours</h1>
            <p className="text-xs text-slate-400">Management Portal</p>
          </div>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800 hidden lg:flex"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        )}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSidebarOpen(false)}
            className="text-white hover:bg-slate-800"
            data-testid="mobile-sidebar-close"
          >
            <X size={20} />
          </Button>
        )}
      </div>

      <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
        {menuItems.map((item) => (
          checkPerm(item.permission) && (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-red-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} />
              {(sidebarOpen || isMobile) && <span>{item.label}</span>}
            </button>
          )
        ))}

        {/* Reports Dropdown - Only show if has reports permission */}
        {checkPerm('reports') && (
          <div>
            <button
              onClick={() => setReportsOpen(!reportsOpen)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                isReportActive()
                  ? 'bg-red-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
              data-testid="nav-reports"
            >
              <div className="flex items-center gap-3">
                <FileText size={20} />
                {(sidebarOpen || isMobile) && <span>Reports</span>}
              </div>
              {(sidebarOpen || isMobile) && (
                <ChevronDown
                  size={16}
                  className={`transition-transform ${reportsOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>
            {(sidebarOpen || isMobile) && reportsOpen && (
              <div className="ml-8 mt-2 space-y-1">
                {reportItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive(item.path)
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                    data-testid={`nav-report-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User Management & Activity Logs - Super Admin Only */}
        {isSuperAdmin() && (
          <>
            <button
              onClick={() => navigate('/admin/users')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive('/admin/users')
                  ? 'bg-red-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
              data-testid="nav-users"
            >
              <Users size={20} />
              {(sidebarOpen || isMobile) && <span>User Management</span>}
            </button>
            <button
              onClick={() => navigate('/admin/activity-logs')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive('/admin/activity-logs')
                  ? 'bg-red-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
              data-testid="nav-activity-logs"
            >
              <Activity size={20} />
              {(sidebarOpen || isMobile) && <span>Activity Logs</span>}
            </button>
          </>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-700">
        {(sidebarOpen || isMobile) && (
          <div className="mb-3">
            <p className="font-medium text-sm text-white">{admin?.name}</p>
            <p className="text-xs text-slate-400">{admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</p>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`${(sidebarOpen || isMobile) ? 'w-full' : 'w-12'} text-red-400 hover:text-red-300 hover:bg-slate-800`}
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          {(sidebarOpen || isMobile) && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSidebarOpen(true)}
            className="text-white hover:bg-slate-800"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </Button>
          <div>
            <h1 className="text-sm font-bold text-red-500">Dnotours</h1>
            <p className="text-[10px] text-slate-400">Management Portal</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          className="text-red-400 hover:text-red-300 hover:bg-slate-800"
          data-testid="mobile-logout-btn"
        >
          <LogOut size={20} />
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setMobileSidebarOpen(false)}
        >
          <aside 
            className="w-72 h-full bg-slate-900 text-white flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 fixed h-full z-50`}>
        <SidebarContent isMobile={false} />
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 pt-14 lg:pt-0 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
